import { defineStore } from 'pinia'

import backgroundTasksApi from '@/api/backgroundtasks'
import { BackgroundTaskAttributes } from 'common'
import type { IndexBackgroundTasks } from 'common'

const useBackgroundTasksStore = defineStore('backgroundtasks', {
  state: () => ({
    backgroundTasks: [] as IndexBackgroundTasks[],
    loading: false,
    results: [] as BackgroundTaskAttributes[],

    // The activePoll is used so that there is only one polling request active
    // at a time. It's a while loop that hits the `index` endpoint until there
    // are no more active or pending tasks. When the loop is complete, the
    // activePoll is set to null in order for it to be started again with `poll`.
    activePoll: null as Promise<void> | null
  }),
  getters: {
    pending(): IndexBackgroundTasks[] {
      return this.backgroundTasks.filter((task) => task.status === 'pending')
    },
    queued(): IndexBackgroundTasks[] {
      return this.backgroundTasks.filter((task) => task.status === 'queued')
    },
    failure(): IndexBackgroundTasks[] {
      return this.backgroundTasks.filter((task) => task.status === 'failure')
    }
  },
  actions: {
    async index() {
      this.loading = true
      try {
        this.backgroundTasks = await backgroundTasksApi.index()
        return this.backgroundTasks
      } finally {
        this.loading = false
      }
    },
    async poll() {
      if (this.activePoll) {
        return this.activePoll
      }

      // Create a new poll and save it to the store.
      this.activePoll = (async () => {
        do {
          await this.index()

          // Wait for the timeout period before checking again.
          const timeout = 5000 // 5 seconds
          await new Promise((resolve) => window.setTimeout(resolve, timeout))
        } while (this.pending.length > 0 || this.queued.length > 0)
      })().finally(() => {
        this.activePoll = null
      })

      return this.activePoll
    },

    show: backgroundTasksApi.show,
    markRead: backgroundTasksApi.markRead
  }
})

export default useBackgroundTasksStore
